import type { LiveEvent, Mood, SayChannel, ServerFrame } from "@tutor/protocol";
import {
  MAX_PAGES,
  type ChatEntry,
  type ItemView,
  type LearnerView,
  type LocalVerdict,
  type WorkLineView,
} from "./types";

/**
 * Changes made on the device before the actor confirms them. Each is later replaced by
 * its persisted event, matched by `clientMsgId`, so a replay never shows a line twice.
 */
export type LocalAction =
  | {
      readonly type: "local.say";
      readonly clientMsgId: string;
      readonly text: string;
      readonly channel?: SayChannel;
    }
  | {
      readonly type: "local.attempt";
      readonly clientMsgId: string;
      readonly itemId: string;
      readonly answer: string;
      readonly local: LocalVerdict;
    }
  | {
      /** The actor acked a help tap without keeping it (ADR-0044): its line goes. */
      readonly type: "local.unsent";
      readonly clientMsgId: string;
    }
  | {
      /** A line of « Mon brouillon »; `work.line` confirms it. */
      readonly type: "local.write";
      readonly clientMsgId: string;
      readonly itemId: string;
      readonly text: string;
    }
  | {
      /** The child's « Explique avec tes mots »; `teachback.graded` settles it. */
      readonly type: "local.explain";
      readonly clientMsgId: string;
      readonly itemId: string;
      readonly text: string;
    }
  | { readonly type: "local.advance" }
  | { readonly type: "local.skipLesson"; readonly itemId: string }
  | {
      /** The child picked the class chapter (ADR-0053); `class.state` confirms it. */
      readonly type: "local.classPick";
      readonly section: string | null;
    }
  | { readonly type: "local.dismissMoment" }
  | {
      /** « Fin » (feature 7); `session.ended` confirms it. */
      readonly type: "local.end";
      readonly sessionStart: number;
      readonly mood: Mood | null;
    };

export type ViewInput = ServerFrame | LocalAction;

/** The view before anything was stored or received. */
export function initialView(): LearnerView {
  return {
    status: "connecting",
    headSeq: 0,
    messages: [],
    item: null,
    upNext: null,
    layout: "chat",
    mode: "text",
    skills: {},
    progress: {},
    moment: null,
    curiosity: true,
    oneBoard: false,
    help: null,
    workbench: null,
    practice: null,
    plan: null,
    opener: null,
    ended: null,
    climbs: {},
    pages: [],
    class: null,
    queued: 0,
  };
}

/**
 * Pure: the next view after one server frame or local action. Unknown or stale input
 * returns the same object, so callers can skip a render with `===`.
 */
export function reduce(view: LearnerView, input: ViewInput): LearnerView {
  switch (input.type) {
    case "event":
      // Older seqs were already applied (replay overlap). A gap is healed by the client,
      // which resumes from `headSeq` before it lets a gapped event through.
      if (input.seq <= view.headSeq) return view;
      return { ...applyEvent(view, input.event, input.at), headSeq: input.seq };
    case "ready":
      return view.status === "live" ? view : { ...view, status: "live" };
    case "stream":
      return applyStream(view, input.turnId, input.offset, input.text, tabOf(input.channel));
    case "ack":
    case "error":
      // Outbox bookkeeping and developer errors: nothing on screen changes.
      return view;
    case "local.say":
      return {
        ...view,
        messages: [
          ...view.messages,
          {
            kind: "learner",
            id: input.clientMsgId,
            text: input.text,
            pending: true,
            ...tabOf(input.channel),
          },
        ],
      };
    case "local.attempt":
      return updateItem(view, input.itemId, (item) => ({
        ...item,
        attempts: [
          ...item.attempts,
          {
            clientMsgId: input.clientMsgId,
            answer: input.answer,
            local: input.local,
            server: null,
          },
        ],
      }));
    case "local.unsent": {
      const messages = view.messages.filter(
        (m) => !(m.kind === "learner" && m.id === input.clientMsgId && m.pending),
      );
      if (messages.length !== view.messages.length) return { ...view, messages };
      // A line the actor did not keep (item gone, or the page is full), or an
      // explanation it did not grade (sent to the conversation, or no longer asked).
      return reopenExplanation(dropPendingLine(view, input.clientMsgId), input.clientMsgId);
    }
    case "local.skipLesson":
      return endLesson(view, input.itemId);
    case "local.explain":
      return updateItem(view, input.itemId, (item) =>
        item.teachBack?.state === "asked"
          ? { ...item, teachBack: { state: "sent", id: input.clientMsgId, text: input.text } }
          : item,
      );
    case "local.write":
      return upsertLine(view, input.itemId, {
        id: input.clientMsgId,
        text: input.text,
        pending: true,
      });
    case "local.advance":
      return view.upNext === null ? view : moveOn(view, view.upNext);
    case "local.dismissMoment":
      return view.moment === null ? view : { ...view, moment: null };
    case "local.end":
      return view.ended?.sessionStart === input.sessionStart
        ? view
        : { ...view, ended: { sessionStart: input.sessionStart, mood: input.mood } };
    case "local.classPick": {
      const now = view.class?.classNow ?? null;
      if (view.class === null || now === null || (now.section ?? null) === input.section) {
        return view;
      }
      // As the actor saves it: the contrôle date goes with the chapter it was for.
      return {
        ...view,
        class: {
          ...view.class,
          classNow: {
            ...now,
            section: input.section,
            testOn: null,
            chosenBy: input.section === null ? null : "child",
          },
        },
      };
    }
  }
  return view;
}

function applyEvent(view: LearnerView, event: LiveEvent, at: string): LearnerView {
  switch (event.type) {
    case "mastery.update":
      return {
        ...view,
        skills: {
          ...view.skills,
          [event.skillId]: {
            pMasteredBefore: event.pMasteredBefore,
            pMastered: event.pMastered,
            at,
          },
        },
      };
    case "session.state":
      return view.mode === event.mode ? view : { ...view, mode: event.mode };
    case "curiosity.state":
      return view.curiosity === event.enabled ? view : { ...view, curiosity: event.enabled };
    case "board.style":
      return view.oneBoard === event.oneBoard ? view : { ...view, oneBoard: event.oneBoard };
    case "practice.state":
      return { ...view, practice: { blocked: event.blocked, day: event.day } };
    case "session.plan": {
      // A new session turns the board to a blank page; the same one only moves its ticks.
      const same = view.plan?.sessionStart === event.sessionStart;
      return {
        ...view,
        plan: { sessionStart: event.sessionStart, goals: event.goals },
        pages: same ? view.pages : [],
        climbs: same ? view.climbs : {},
        opener: view.opener?.sessionStart === event.sessionStart ? view.opener : null,
        ended: view.ended?.sessionStart === event.sessionStart ? view.ended : null,
      };
    }
    case "session.ended":
      return { ...view, ended: { sessionStart: event.sessionStart, mood: event.mood } };
    case "session.offer":
      return {
        ...view,
        opener: {
          sessionStart: event.sessionStart,
          lastTime: event.lastTime,
          ...(event.typed === true ? { typed: true } : {}),
        },
      };
    case "canvas.widget":
      return { ...view, workbench: { itemId: event.itemId, widget: event.widget } };
    case "help.offer":
      return { ...view, help: { itemId: event.itemId, options: event.options } };
    case "learner.message":
      return upsertEntry(view, "learner", event.clientMsgId, {
        kind: "learner",
        id: event.clientMsgId,
        text: event.text,
        pending: false,
        ...tabOf(event.channel),
      });
    case "tutor.message":
      return upsertEntry(coachAnswered(view), "tutor", event.turnId, {
        kind: "tutor",
        id: event.turnId,
        text: event.text,
        widget: event.widget,
        hint: event.hint,
        streaming: false,
        failed: null,
        ...tabOf(event.channel),
      });
    case "turn.failed": {
      const existing = findTutor(view, event.turnId);
      return upsertEntry(view, "tutor", event.turnId, {
        kind: "tutor",
        id: event.turnId,
        // Keep what was streamed: the child already read it.
        text: existing?.text ?? "",
        widget: null,
        hint: null,
        streaming: false,
        failed: event.reason,
        ...tabOf(event.channel),
      });
    }
    case "item.presented": {
      const item: ItemView = {
        itemId: event.itemId,
        skillId: event.skillId,
        prompt: event.prompt,
        answer: event.answer,
        check: event.check,
        moment: event.moment ?? "try",
        attempts: [],
        lines: [],
        mark: null,
        steps: [],
        done: false,
      };
      // A new exercise starts with an empty workbench, even when the same item returns.
      // Prefetch: the finished item stays on screen with its result until `advance()`.
      // An item on its way means practice is open again, whatever blocked it before.
      // After « On reprend » the session starts on this one: last time's finished
      // exercise does not wait for « Suivant ».
      if (view.item?.done === true && view.opener === null) {
        return { ...view, upNext: item, workbench: null, practice: null };
      }
      return { ...view, item, upNext: null, workbench: null, practice: null, opener: null };
    }
    case "item.advanced":
      // Already moved when this device tapped « Suivant »; another device, or a replay
      // after a reload, catches up here.
      return view.upNext?.itemId === event.itemId ? moveOn(view, view.upNext) : view;
    case "item.graded": {
      const skillId = [view.item, view.upNext].find((i) => i?.itemId === event.itemId)?.skillId;
      const counted = keepBench(applyGrade(view, event), event);
      const graded =
        skillId === undefined || Object.hasOwn(counted.climbs, skillId)
          ? counted
          : {
              ...counted,
              climbs: { ...counted.climbs, [skillId]: view.progress[skillId]?.step ?? 0 },
            };
      const progress = event.progress;
      if (progress === undefined || progress === null) return graded;
      if (skillId === undefined) return graded;
      return {
        ...graded,
        progress: { ...graded.progress, [skillId]: progress },
        moment:
          progress.milestone === null
            ? graded.moment
            : { skillId, milestone: progress.milestone, at },
      };
    }
    case "lesson.started":
      return updateItem(view, event.itemId, (item) =>
        item.done || item.lesson !== undefined
          ? item
          : {
              ...item,
              lesson: {
                page: event.page ?? null,
                question: null,
                choices: [],
                answers: [],
                done: false,
              },
            },
      );
    case "lesson.check":
      return updateItem(view, event.itemId, (item) =>
        item.lesson === undefined || item.lesson.question === null
          ? {
              ...item,
              lesson: {
                page: item.lesson?.page ?? null,
                question: event.question,
                choices: event.choices,
                answers: [],
                done: item.lesson?.done ?? false,
              },
            }
          : item,
      );
    case "lesson.checked":
      return updateItem(view, event.itemId, (item) =>
        item.lesson === undefined
          ? item
          : {
              ...item,
              lesson: {
                ...item.lesson,
                answers: [...item.lesson.answers, { answer: event.answer, right: event.right }],
              },
            },
      );
    case "lesson.done":
      return endLesson(view, event.itemId);
    case "teachback.ask":
      return updateItem(view, event.itemId, (item) =>
        item.teachBack === undefined ? { ...item, teachBack: { state: "asked" } } : item,
      );
    case "teachback.graded": {
      const skillId = [view.item, view.upNext].find((i) => i?.itemId === event.itemId)?.skillId;
      const graded = updateItem(view, event.itemId, (item) => ({
        ...item,
        teachBack: { state: "graded", text: event.text, counted: event.counted },
      }));
      const progress = event.progress;
      if (skillId === undefined || progress === undefined || progress === null) return graded;
      return {
        ...graded,
        progress: { ...graded.progress, [skillId]: progress },
        moment:
          progress.milestone === null
            ? graded.moment
            : { skillId, milestone: progress.milestone, at },
      };
    }
    case "work.line":
      return upsertLine(view, event.itemId, {
        id: event.clientMsgId,
        text: event.text,
        pending: false,
      });
    case "canvas.step":
      return updateItem(view, event.itemId, (item) =>
        item.steps.some((step) => step.turnId === event.turnId)
          ? item
          : { ...item, steps: [...item.steps, { turnId: event.turnId, text: event.text }] },
      );
    case "work.mark":
      return updateItem(view, event.itemId, (item) => ({ ...item, mark: event.lineId ?? null }));
    case "ui.layout":
      return { ...view, layout: event.mode };
    case "class.state":
      return { ...view, class: { classNow: event.classNow ?? null, chapters: event.chapters } };
  }
  return view;
}

/** The entry field for a tab: only a free question carries one. */
function tabOf(channel: SayChannel | null | undefined): { readonly channel?: SayChannel } {
  return channel === "curiosity" ? { channel } : {};
}

function applyStream(
  view: LearnerView,
  turnId: string,
  offset: number,
  text: string,
  tab: { readonly channel?: SayChannel },
): LearnerView {
  const existing = findTutor(view, turnId);
  if (existing === undefined) {
    // Joining mid-turn: only the offset-0 resend gives the whole text so far.
    if (offset !== 0) return view;
    return {
      ...view,
      messages: [
        ...view.messages,
        {
          kind: "tutor",
          id: turnId,
          text,
          widget: null,
          hint: null,
          streaming: true,
          failed: null,
          ...tab,
        },
      ],
    };
  }
  // A finished turn is final; a late piece must not reopen it.
  if (!existing.streaming) return view;
  let next: string;
  if (offset === 0) next = text;
  else if (offset === existing.text.length) next = existing.text + text;
  else return view;
  return upsertEntry(view, "tutor", turnId, { ...existing, text: next });
}

type TutorEntry = Extract<ChatEntry, { kind: "tutor" }>;

function findTutor(view: LearnerView, turnId: string): TutorEntry | undefined {
  for (const entry of view.messages) {
    if (entry.kind === "tutor" && entry.id === turnId) return entry;
  }
  return undefined;
}

/** Replaces the entry of that kind and id in place, or appends it. */
function upsertEntry(
  view: LearnerView,
  kind: ChatEntry["kind"],
  id: string,
  entry: ChatEntry,
): LearnerView {
  const index = view.messages.findIndex((m) => m.kind === kind && m.id === id);
  if (index === -1) return { ...view, messages: [...view.messages, entry] };
  return { ...view, messages: view.messages.map((m, i) => (i === index ? entry : m)) };
}

function applyGrade(
  view: LearnerView,
  event: Extract<LiveEvent, { type: "item.graded" }>,
): LearnerView {
  return updateItem(view, event.itemId, (item) => {
    const known = item.attempts.some((a) => a.clientMsgId === event.clientMsgId);
    const attempts = known
      ? item.attempts.map((a) =>
          a.clientMsgId === event.clientMsgId ? { ...a, server: event.correct } : a,
        )
      : // Answered on another device, or the local copy was lost: show the grade anyway.
        [
          ...item.attempts,
          {
            clientMsgId: event.clientMsgId,
            answer: "",
            local: "unknown" as const,
            server: event.correct,
          },
        ];
    const solution = event.solution ?? null;
    const hint = event.hint ?? null;
    return {
      ...item,
      attempts,
      done: event.done,
      coachAnswered: false,
      ...(solution === null ? {} : { solution }),
      ...(hint === null ? {} : { hint }),
    };
  });
}

/** Coach wrote after the open exercise's last graded try: its words are the feedback now. */
function coachAnswered(view: LearnerView): LearnerView {
  const item = view.item;
  if (item?.coachAnswered !== false) return view;
  return { ...view, item: { ...item, coachAnswered: true } };
}

/** The next item takes the canvas; the finished one becomes this session's last page. */
function moveOn(view: LearnerView, next: ItemView): LearnerView {
  const finished = view.item;
  const pages = finished?.done === true ? [...view.pages, finished].slice(-MAX_PAGES) : view.pages;
  return { ...view, item: next, upNext: null, pages };
}

/** A finished item keeps its workbench widget for its page (ADR-0052). */
function keepBench(
  view: LearnerView,
  event: Extract<LiveEvent, { type: "item.graded" }>,
): LearnerView {
  const bench = view.workbench;
  if (!event.done || bench?.itemId !== event.itemId) return view;
  return updateItem(view, event.itemId, (item) => ({ ...item, bench: bench.widget }));
}

/** Replaces the line with that id in place, or appends it, on the item or its page. */
function upsertLine(view: LearnerView, itemId: string, line: WorkLineView): LearnerView {
  return updateItem(view, itemId, (item) => {
    const index = item.lines.findIndex((l) => l.id === line.id);
    const lines =
      index === -1 ? [...item.lines, line] : item.lines.map((l, i) => (i === index ? line : l));
    return { ...item, lines };
  });
}

/** An explanation the actor acked without grading goes back to the question. */
function reopenExplanation(view: LearnerView, id: string): LearnerView {
  const item = view.item;
  const teachBack = item?.teachBack;
  if (item === null || teachBack?.state !== "sent" || teachBack.id !== id) return view;
  return { ...view, item: { ...item, teachBack: { state: "asked" } } };
}

function dropPendingLine(view: LearnerView, id: string): LearnerView {
  const item = view.item;
  if (item === null) return view;
  const lines = item.lines.filter((l) => !(l.id === id && l.pending));
  return lines.length === item.lines.length ? view : { ...view, item: { ...item, lines } };
}

/** « Je vérifie » is over: the exercise is the child's to try. */
function endLesson(view: LearnerView, itemId: string): LearnerView {
  return updateItem(view, itemId, (item) =>
    item.lesson === undefined || item.lesson.done
      ? item
      : { ...item, lesson: { ...item.lesson, done: true } },
  );
}

function updateItem(
  view: LearnerView,
  itemId: string,
  change: (item: ItemView) => ItemView,
): LearnerView {
  if (view.item?.itemId === itemId) return { ...view, item: change(view.item) };
  if (view.upNext?.itemId === itemId) return { ...view, upNext: change(view.upNext) };
  return view;
}
